import { EarningsPeriod } from '@api/api/earnings';
import { Icon } from '@design/components/icon/Icon';
import { Formats, formatDateToLocale } from '@helpers/utils/dates/dates';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import {
  formatNumberWithUnit,
  getReportingTimeIcon,
  type ReportingTimeIcon,
} from '@terminal/widgets/earnings/utils/utils';
import { WithNAFallback } from '@toggle/design-system/src/components/table';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styles from './EarningsInsightSection.module.scss';

export interface PreviewContentProps {
  earningsData: EarningsPeriod;
  metricLabelClassName?: string;
  reportingTimeIconColorByIcon: Record<ReportingTimeIcon, string>;
}

export const PreviewContent = ({
  earningsData,
  metricLabelClassName,
  reportingTimeIconColorByIcon,
}: PreviewContentProps) => {
  const { t } = useTranslation(['widget']);
  const languageSetting = useLanguageStore(state => state.languageSetting);

  const formattedDate = earningsData.reporting_date
    ? formatDateToLocale(
        languageSetting,
        new Date(earningsData.reporting_date),
        Formats['MMMM d']
      )
    : '-';

  const reportPeriod =
    earningsData.fiscal_period && earningsData.fiscal_year
      ? `${earningsData.fiscal_period} ${earningsData.fiscal_year}`
      : '-';
  const metricLabelClasses = clsx(styles.metricLabel, metricLabelClassName);
  const timeIcon = getReportingTimeIcon(new Date(earningsData.reporting_date));

  return (
    <>
      <div className={styles.metricContainer}>
        <span className={metricLabelClasses}>
          {t('widget:assetOverview.earningsSection.nextReportDate')}
        </span>
        <div className={styles.dateContainer}>
          <span className={styles.metricValue}>{formattedDate}</span>
          {timeIcon && (
            <Icon
              iconName={timeIcon}
              size={16}
              fillColor={reportingTimeIconColorByIcon[timeIcon]}
            />
          )}
        </div>
      </div>
      <div className={styles.metricContainer}>
        <span className={metricLabelClasses}>
          {t('widget:assetOverview.earningsSection.reportPeriod')}
        </span>
        <span className={styles.metricValueMono}>{reportPeriod}</span>
      </div>
      <div className={styles.metricContainer}>
        <span className={metricLabelClasses}>
          {t('widget:assetOverview.earningsSection.epsEstimate')}
        </span>
        <span className={styles.metricValueMono}>
          <WithNAFallback
            value={
              <>
                {formatNumberWithUnit(earningsData.eps?.estimate)}
                <span className={styles.currencyLabel}>
                  {earningsData.currency ?? 'USD'}
                </span>
              </>
            }
            check={earningsData.eps?.estimate !== undefined}
          />
        </span>
      </div>
      <div className={styles.metricContainer}>
        <span className={metricLabelClasses}>
          {t('widget:assetOverview.earningsSection.revenueEstimate')}
        </span>
        <span className={styles.metricValueMono}>
          <WithNAFallback
            value={
              <>
                {formatNumberWithUnit(earningsData.revenue?.estimate)}
                <span className={styles.currencyLabel}>
                  {earningsData.currency ?? 'USD'}
                </span>
              </>
            }
            check={earningsData.revenue?.estimate !== undefined}
          />
        </span>
      </div>
    </>
  );
};
